import axios from 'axios';
import get from 'lodash.get';
import merge from 'lodash.merge';
import cloneDeep from 'lodash.clonedeep';
import axiosRetry from 'axios-retry';
import ApiError from '@/models/ApiError';
import { installInterceptors } from '@/api/interceptors';

/**
 * @class BaseApiClient
 * @description Base networking client used to interface with HTTP servers.
*/
class BaseApiClient {
  constructor(baseUri, headers, pathToApiErrors = 'response.data.error.message') {
    this.baseUri = baseUri;
    this.headers = headers;
    this.axiosInstance = axios.create({
      baseURL: this.baseUri,
      headers: this.headers,
      formSerializer: {
        dots: true,
      },
    });
    installInterceptors(this.axiosInstance);
    this.pathToApiErrors = pathToApiErrors;
    /* Refer to https://github.com/softonic/axios-retry#options - ETC
     * By default, we will disable retries for all requests, but we still
     * define some sensible defaults in case someone decides to customize
     * retries on a per-request basis.
     * setting the retryCondition to always be true, otherwise the default logic is:
     * "By default, it retries if it is a network error or a 5xx error on an idempotent
     * request (GET, HEAD, OPTIONS, PUT or DELETE)."
     * Since Some API's (Easy-Pass) still do not deploy cleanly,
     * we should just let it retry everything.
     */
    axiosRetry(this.axiosInstance, {
      retries: 0,
      retryCondition: () => true,
      retryDelay: axiosRetry.exponentialDelay,
    });
  }

  get(url, params, headers, cancelToken, retries = 0) {
    const config = {
      headers,
      url: `${this.baseUri}${url}`,
      method: 'GET',
      params,
      cancelToken,
      'axios-retry': {
        retries,
      },
      paramsSerializer: {
        indexes: null, // by default: false
      },
    };

    return this.request(config);
  }

  downloadFile(url, params, headers, cancelToken, retries = 0) {
    const config = {
      headers,
      url: `${this.baseUri}${url}`,
      method: 'GET',
      params,
      cancelToken,
      responseType: 'blob',
      'axios-retry': {
        retries,
      },
    };

    return this.request(config);
  }

  post(url, data, headers, cancelToken, retries = 0) {
    const config = {
      headers,
      url: `${this.baseUri}${url}`,
      method: 'POST',
      data,
      cancelToken,
      'axios-retry': {
        retries,
      },
    };

    return this.request(config);
  }

  patch(url, data, headers, cancelToken, retries = 0) {
    const config = {
      headers,
      url: `${this.baseUri}${url}`,
      method: 'PATCH',
      data,
      cancelToken,
      'axios-retry': {
        retries,
      },
    };

    return this.request(config);
  }

  uploadFile(url, data, params, headers, cancelToken, retries = 0) {
    const config = {
      headers: merge({}, cloneDeep(headers), { 'Content-Type': 'multipart/form-data' }),
      url: `${this.baseUri}${url}`,
      method: 'POST',
      data,
      params,
      cancelToken,
      'axios-retry': {
        retries,
      },
    };

    return this.request(config);
  }

  put(url, data, headers, cancelToken, retries = 0) {
    const config = {
      headers,
      url: `${this.baseUri}${url}`,
      method: 'PUT',
      data,
      cancelToken,
      'axios-retry': {
        retries,
      },
    };

    return this.request(config);
  }

  async putFile(url, data, headers, addlConfig = {}) {
    const resp = await this.axiosInstance.put(
      url,
      data,
      {
        headers,
        ...addlConfig,
      },
    );

    return resp.data;
  }

  delete(
    url,
    data,
    headers,
    /* eslint-disable-next-line default-param-last */
    params = {},
    cancelToken,
    retries = 0,
  ) {
    const config = {
      headers,
      url: `${this.baseUri}${url}`,
      method: 'DELETE',
      data,
      params,
      cancelToken,
      'axios-retry': {
        retries,
      },
    };

    return this.request(config);
  }

  request(config) {
    const configWithHeaders = config;
    configWithHeaders.headers = merge(this.headers, config.headers);
    if (!config.cancelToken) {
      configWithHeaders.cancelToken = this.getCancelToken().token;
    }
    return this.axiosInstance.request(configWithHeaders)
      .catch((error) => {
        if (axios.isCancel(error) || error.message === 'canceled') {
          return Promise.reject(error);
        }
        const errorMessage = get(
          error,
          this.pathToApiErrors,
          'There was problem processing your request. Please try again or adjust your parameters.',
        );
        const e = new ApiError(errorMessage, error);
        return Promise.reject(e);
      });
  }

  // eslint-disable-next-line
  getCancelToken() {
    return axios.CancelToken.source();
  }
}

export default BaseApiClient;
